import { create } from "zustand";

type ModalState =
  | "idle"
  | "error"
  | "details"
  | "create"
  | "execution"
  | "validation-success"
  | "error-summary"
  | "test-input"
  | "search-nodes";

interface UIState {
  selectedErrors?: Array<{ message: string; type: string; node: string }>;
  modalState: ModalState;

  // Flow name
  flowName: string;

  // Flow blocked state - blocks editing actions when true (published or editing by another user)
  isFlowBlocked: boolean;

  // Panel states
  sidebarOpen: boolean;
  nodeCreatorOpen: boolean;
  nodeDetailsOpen: boolean;
  timerDetailsOpen: boolean;
  executionDetailsOpen?: boolean;

  // Selection and interaction
  selectedNodeId?: string;
  lastInteractedNodeId?: string;
  hoveredNodeId?: string;

  // Context menu
  contextMenu?: {
    x: number;
    y: number;
    nodeId?: string;
    type: "node" | "canvas" | "edge";
  };

  // Connection splitting
  connectionToSplit?: {
    edgeId: string;
    sourceNodeId: string;
    targetNodeId: string;
    sourceHandle: string;
    targetHandle: string;
  };

  // Canvas state
  viewport: {
    x: number;
    y: number;
    zoom: number;
  };

  // Loading states
  isLoading: boolean;

  nodesWithErrors: Map<
    string,
    {
      0?: string;
      message: string;
      type: string;
      node: string;
    }[]
  >;

  setNodesWithErrors: (
    nodesWithErrors: Map<
      string,
      {
        message: string;
        type: string;
        node: string;
      }[]
    >,
  ) => void;

  clearErrors: () => void;

  // Actions
  setSidebarOpen: (open: boolean) => void;
  setNodeCreatorOpen: (open: boolean) => void;
  setNodeDetailsOpen: (open: boolean) => void;
  setTimerDetailsOpen: (open: boolean) => void;

  setSelectedNodeId: (nodeId?: string) => void;
  setLastInteractedNodeId: (nodeId?: string) => void;
  setHoveredNodeId: (nodeId?: string) => void;

  openContextMenu: (
    x: number,
    y: number,
    type: "node" | "canvas" | "edge",
    nodeId?: string,
  ) => void;
  closeContextMenu: () => void;

  setConnectionToSplit: (connection?: {
    edgeId: string;
    sourceNodeId: string;
    targetNodeId: string;
    sourceHandle: string;
    targetHandle: string;
  }) => void;

  setViewport: (viewport: { x: number; y: number; zoom: number }) => void;
  setLoading: (loading: boolean) => void;

  // Reset functions
  resetSelection: () => void;
  resetInteraction: () => void;
  setExecutionDetailsOpen: (open: boolean) => void;
  setFlowName: (name: string) => void;
  setIsFlowBlocked: (isBlocked: boolean) => void;
  reset: () => void;
  setModalState: (state: ModalState) => void;
  setSelectedErrors: (
    errors?: Array<{ message: string; type: string; node: string }>,
  ) => void;

  changeCounter: number;
  incrementChangeCounter: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  // Initial state
  modalState: "idle",
  nodesWithErrors: new Map(),
  sidebarOpen: true,
  nodeCreatorOpen: false,
  nodeDetailsOpen: false,
  timerDetailsOpen: false,
  executionDetailsOpen: false,

  selectedNodeId: undefined,
  lastInteractedNodeId: undefined,
  hoveredNodeId: undefined,

  contextMenu: undefined,
  connectionToSplit: undefined,
  flowName: "",
  isFlowBlocked: false,

  viewport: {
    x: 100,
    y: 100,
    zoom: 1,
  },

  isLoading: false,

  // Panel actions
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
  setNodeCreatorOpen: (open: boolean) =>
    set({
      nodeCreatorOpen: open,
      modalState: open ? "create" : "idle",
    }),
  setNodeDetailsOpen: (open: boolean) =>
    set({
      nodeDetailsOpen: open,
      modalState: open ? "details" : "idle",
    }),
  setTimerDetailsOpen: (open: boolean) => set({ timerDetailsOpen: open }),
  setExecutionDetailsOpen: (open: boolean) =>
    set({
      executionDetailsOpen: open,
      modalState: open ? "execution" : "idle",
    }),

  // Selection actions
  setSelectedNodeId: (nodeId?: string) => set({ selectedNodeId: nodeId }),
  setLastInteractedNodeId: (nodeId?: string) =>
    set({ lastInteractedNodeId: nodeId }),
  setHoveredNodeId: (nodeId?: string) => set({ hoveredNodeId: nodeId }),

  // Context menu actions
  openContextMenu: (
    x: number,
    y: number,
    type: "node" | "canvas" | "edge",
    nodeId?: string,
  ) =>
    set({
      contextMenu: {
        x,
        y,
        type,
        nodeId,
      },
    }),
  closeContextMenu: () => set({ contextMenu: undefined }),

  // Connections
  setConnectionToSplit: (connection) => set({ connectionToSplit: connection }),

  // Viewport
  setViewport: (viewport) => set({ viewport }),

  // Loading
  setLoading: (loading: boolean) => set({ isLoading: loading }),

  // Reset functions
  resetSelection: () =>
    set({
      selectedNodeId: undefined,
      nodeDetailsOpen: false,
      timerDetailsOpen: false,
    }),
  resetInteraction: () =>
    set({
      lastInteractedNodeId: undefined,
      hoveredNodeId: undefined,
      contextMenu: undefined,
    }),

  // Flow
  setFlowName: (name: string) => set({ flowName: name }),
  setIsFlowBlocked: (isBlocked: boolean) => set({ isFlowBlocked: isBlocked }),
  reset: () =>
    set({
      sidebarOpen: false,
      nodeCreatorOpen: false,
      nodeDetailsOpen: false,
      timerDetailsOpen: false,
      selectedNodeId: undefined,
      lastInteractedNodeId: undefined,
      hoveredNodeId: undefined,
      contextMenu: undefined,
      connectionToSplit: undefined,
      flowName: "",
      isFlowBlocked: false,
      viewport: { x: 100, y: 100, zoom: 1 },
      isLoading: false,
      nodesWithErrors: new Map(),
      modalState: "idle",
      selectedErrors: undefined,
      changeCounter: 0,
    }),

  setNodesWithErrors: (nodesWithErrors) =>
    set({
      nodesWithErrors,
    }),

  clearErrors: () =>
    set({
      nodesWithErrors: new Map(),
      selectedErrors: undefined,
      modalState: "idle",
    }),

  setModalState: (state) => set({ modalState: state }),
  setSelectedErrors: (errors) => set({ selectedErrors: errors }),

  changeCounter: 0,
  incrementChangeCounter: () =>
    set((state) => ({ changeCounter: state.changeCounter + 1 })),
}));
